import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  TextField,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ImageIcon from '@mui/icons-material/Image';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useAuth } from '../../contexts/AuthContext';
import { createItem, uploadItemFile } from '../../services/items';
import type { ItemType } from '../../types';
import UrlItemForm from './UrlItemForm';
import TextItemForm from './TextItemForm';
import FileItemForm from './FileItemForm';

interface CreateItemDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (itemId: string) => void;
}

export default function CreateItemDialog({ open, onClose, onCreated }: CreateItemDialogProps) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const [type, setType] = useState<ItemType>('url');
  const [formData, setFormData] = useState({ title: '', content: '' });
  const [file, setFile] = useState<File | undefined>();
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);

  const handleFileData = (data: { title: string; content: string; file?: File }) => {
    setFormData({ title: data.title, content: data.content });
    setFile(data.file);
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const itemType: ItemType = file && type !== 'url' && type !== 'text'
        ? (file.type.startsWith('image/') ? 'image' : 'file')
        : type;

      const itemId = await createItem(user.uid, {
        type: itemType,
        title: formData.title,
        content: formData.content,
        tags,
        mimeType: file?.type,
        fileName: file?.name,
        fileSize: file?.size,
      });

      if (file) {
        await uploadItemFile(user.uid, itemId, file);
      }

      onCreated(itemId);
      handleClose();
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({ title: '', content: '' });
    setFile(undefined);
    setTags([]);
    setTagInput('');
    setType('url');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" fullScreen={mobile}>
      <DialogTitle>Add Item</DialogTitle>
      <form onSubmit={(e) => { e.preventDefault(); if (!saving && (formData.content || file)) handleSave(); }}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <ToggleButtonGroup
              value={type}
              exclusive
              onChange={(_, v) => v && setType(v)}
              size="small"
              fullWidth
            >
              <ToggleButton value="url"><LinkIcon sx={{ mr: 1 }} />URL</ToggleButton>
              <ToggleButton value="text"><TextFieldsIcon sx={{ mr: 1 }} />Text</ToggleButton>
              <ToggleButton value="image"><ImageIcon sx={{ mr: 1 }} />Image</ToggleButton>
              <ToggleButton value="file"><AttachFileIcon sx={{ mr: 1 }} />File</ToggleButton>
            </ToggleButtonGroup>

            {type === 'url' && <UrlItemForm onDataChange={setFormData} />}
            {type === 'text' && <TextItemForm onDataChange={setFormData} />}
            {(type === 'image' || type === 'file') && (
              <FileItemForm onDataChange={handleFileData} />
            )}

            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={tags}
              onChange={(_, newValue) => setTags(newValue)}
              inputValue={tagInput}
              onInputChange={(_, value) => setTagInput(value)}
              renderInput={(params) => (
                <TextField {...params} label="Tags" placeholder="Add tags..." />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || (!formData.content && !file)}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
